<script>
  import { layoutMethods, customerListLiteComputed, customerLiteMethods } from "@/state/helpers";
  import Swal from 'sweetalert2';
  import cifModalLogs from "@/views/pages/_version_2_/peddlr-lite/merchant/profileInformation/cifModal/index.vue";
  // Account Type "User Documents" tab — per-change requirement-document history (wired to
  // getAccountTypeDocumentHistory / downloadAccountTypeDocument). TIER_LABELS + formatFileSize are static
  // config (tier label names + file-size formatting), not mock data.
  import {
    TIER_LABELS,
    formatFileSize,
  } from "@/views/pages/_version_2_/peddlr-lite/merchant/profileInformation/accountType/accountTypeMock";

  /**
   * Profile Information component
   */
  export default {
    // props: {
    //   mainData: {
    //     type: Object
    //   }
    // },
    page: {
      title: "Profile Information"
    },
    components: {
      cifModalLogs
    },
    computed: {
      ...customerListLiteComputed
    },
    data() {
      return {
        title: "Profile Information",
        infoType: null,
        isFulldetails: false,
        // Account Type "User Documents" tab: the account-type change history (fetched on mount).
        accountTypeHistory: [],
        accountTypeHistoryLoading: false,
      };
    },
    methods: {
      ...layoutMethods,
      ...customerLiteMethods,
      // --- Account Type "User Documents" tab helpers ---
      tierLabel(tier) {
        return TIER_LABELS[tier] || tier || '--';
      },
      formatDocSize(bytes) {
        return formatFileSize(bytes);
      },
      formatChangeDateTime(value) {
        if (!value) return '--';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return '--';
        return d.toLocaleString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: 'numeric', minute: '2-digit', hour12: true,
        });
      },
      // Resolve ref_business.account_id from the loaded profile (fallback to the route :bid).
      accountTypeBusinessId() {
        return this.businessDetails && this.businessDetails.businessId
          ? this.businessDetails.businessId
          : this.$route.params.bid;
      },
      loadAccountTypeHistory() {
        const businessId = this.accountTypeBusinessId();
        if (this.isEmpty(businessId)) return;
        this.accountTypeHistoryLoading = true;
        this.getAccountTypeDocumentHistory({ businessId })
          .then((history) => {
            this.accountTypeHistory = Array.isArray(history) ? history : [];
          })
          .catch((err) => {
            console.log("Account type history fetch failed:", err);
            this.accountTypeHistory = [];
          })
          .finally(() => {
            this.accountTypeHistoryLoading = false;
          });
      },
      onDownloadAccountTypeDoc(doc) {
        this.downloadAccountTypeDocument({ documentId: doc.documentId })
          .then((res) => {
            if (res && res.url) {
              window.open(res.url, '_blank');
            }
          })
          .catch((err) => {
            console.log("Account type document download failed:", err);
            Swal.fire("Download Failed", `Unable to download ${doc.fileName}. Please try again.`, "warning");
          });
      },
      isEmpty (value) {
          return (
            // null or undefined
            (typeof value == "undefined") || 
            (value == undefined) ||
            (value == null) ||
            (value === "") ||
            // has length and it's zero
            // (value.hasOwnProperty('length') && value.length === 0) ||
            (Object.prototype.hasOwnProperty.call(value, 'length') && value.length === 0) ||
            // is an Object and has no keys
            (value.constructor === Object && Object.keys(value).length === 0)
          )
      },
      isValidDate(value) {
        // Reject 0 / "0" / MySQL zero-dates so a bad dob renders "--" instead of "0".
        if (this.isEmpty(value) || value === 0 || value === '0' || String(value).startsWith('0000-00-00')) return false;
        return !Number.isNaN(Date.parse(value));
      },
      openModal(modal=''){
        this.$bvModal.show(modal);
      },
      closeModal(modal=''){
        this.$bvModal.hide(modal);
      },
    },
    watch: {
      // businessDetails loads asynchronously (parent fetch). Load the account-type history once the
      // business id is available.
      'businessDetails.businessId': {
        immediate: true,
        handler(id) {
          if (!this.isEmpty(id)) this.loadAccountTypeHistory();
        }
      }
    },
    async mounted(){
      this.infoType = this.$route.params.tab;
      // Fallback: if businessDetails isn't populated yet but the route has :bid, fetch using the route id.
      if (this.isEmpty(this.businessDetails && this.businessDetails.businessId) && !this.isEmpty(this.$route.params.bid)) {
        this.loadAccountTypeHistory();
      }
    }
  }
    
</script>

<template>
  <div><!-- outer div-->
    <div class="card">
      <div class="card-body">
        <b-tabs
          content-class="p-3 text-muted"
          nav-class="nav-tabs-custom">
          <b-tab :active="infoType == 'Profile'"><!-- profile information -->
            <template v-slot:title>
              <span class="d-none d-sm-inline-block">User Information</span>
            </template>

            <div class="row">
              <div class="col-md-4 col-sm-6 col-xs-12 margin-bottom-20"><!-- email address-->
                <p class="m-0 font-size-12">Email Address</p>
                <p class="font-size-15 text-dark">{{isEmpty(businessDetails.subscriberEmail) ? '--' : businessDetails.subscriberEmail}}</p>
              </div><!-- email address-->
              <div class="col-md-4 col-sm-6 col-xs-12"><!-- DOB-->
                <p class="m-0 font-size-12">Date of Birth</p>
                <p class="font-size-15 text-dark">{{ isValidDate(businessDetails.subscriberDob) ? (businessDetails.subscriberDob | formatDateOnly) : '--' }}</p>
              </div><!-- DOB-->
              <div class="col-md-4 col-sm-6 col-xs-12"><!-- phone number -->
                <p class="m-0 font-size-12">Account Mobile Number</p>
                <p class="font-size-15 text-dark margin-bottom-8">{{isEmpty(businessDetails.subscriberMobileNumber) ? '--' : businessDetails.subscriberMobileNumber}}</p>
                <p class="m-0 font-size-10">Used for login</p>
              </div><!-- phone number -->
              <div class="col-md-4 col-sm-6 col-xs-12 margin-bottom-20"><!-- City of Birth-->
                <p class="m-0 font-size-12">City of Birth</p>
                <p class="font-size-15 text-dark">{{isEmpty(businessDetails.subscriberBirthplace) ? '--' : businessDetails.subscriberBirthplace}}</p>
              </div><!-- City of Birth-->
              <div class="col-md-4 col-sm-6 col-xs-12"><!-- country of birth-->
                <p class="m-0 font-size-12">Country of Birth (Nationality)</p>
                <p class="font-size-15 text-dark">{{isEmpty(businessDetails.subscriberNationality) ? '--' : businessDetails.subscriberNationality}}</p>
              </div><!-- country of birth-->
              <div class="col-md-4 col-sm-6 col-xs-12"><!-- phone number -->
                <p class="m-0 font-size-12">One Time Password</p>
                <p class="font-size-15 text-dark margin-bottom-8">{{isEmpty(businessDetails.subscriberMobileNumber) ? '--' : businessDetails.subscriberMobileNumber}}</p>
                <p class="m-0 font-size-10">For OTP verification</p>
              </div><!-- phone number -->
              <div class="col-md-8"><!-- Complete Home Address-->
                <p class="m-0 font-size-12">Complete Home Address</p>
                <p class="font-size-15 text-dark">{{isEmpty(businessDetails.subscriberAddress) ? '--' : businessDetails.subscriberAddress}}</p>
              </div><!-- Complete Home Address-->
              <div class="col-md-4 col-sm-6 col-xs-12"><!-- kankolek phone number -->
                <p class="m-0 font-size-12">Kankolek Mobile Number</p>
                <p class="font-size-15 text-dark margin-bottom-8">{{isEmpty(businessDetails.subscriberMobileNumber) ? '--' : businessDetails.subscriberMobileNumber}}</p>
                <p class="m-0 font-size-10">Linked to Kankolek</p>
              </div><!-- phone number -->
            </div>
          </b-tab><!-- profile information -->

          <b-tab :active="infoType != 'Profile'"><!-- business information -->
            <template v-slot:title>
              <span class="d-none d-sm-inline-block">Business Information</span>
            </template>

            <div class="row">
              <div class="col-sm-6 col-xs-12"><!-- Business Name-->
                <p class="m-0 font-size-12">Business Name</p>
                <p class="mb-3 font-size-15 text-dark text-capitalize">{{isEmpty(businessDetails.businessName) ? '--' : businessDetails.businessName}}</p>
                <p class="m-0 font-size-12">Business Number</p>
                <p class="mb-3 font-size-15 text-dark">{{isEmpty(businessDetails.businessMobileNumber) ? '--' : businessDetails.businessMobileNumber}}</p>
              </div><!-- Business Name-->
              <div class="col-sm-6 col-xs-12"><!-- business logo -->
                <img
                    :src="businessDetails.businessBusinessLogo"
                    alt=""
                    class="rounded"
                    width="100px"
                    @error="
                      (e) =>
                        (e.target.src = require('@/assets/images/img-placeholder.png'))
                    "
                />
              </div><!-- business logo -->
              <div class="col-sm-6 col-xs-12"><!-- Business Hours-->
                <p class="m-0 font-size-12">Business Hours</p>
                <p class="mb-3 font-size-15 text-dark">{{isEmpty(businessDetails.businessHours) ? '--' : businessDetails.businessHours}}</p>
              </div><!-- Business Hours-->
              <div class="col-sm-6 col-xs-12"><!-- Business Type-->
                <p class="m-0 font-size-12">Business Type</p>
                <p class="mb-3 font-size-15 text-dark">{{isEmpty(businessDetails.businessType) ? '--' : businessDetails.businessType}}</p>
              </div><!-- Business Type-->
              <div class="col-sm-12 col-xs-12"><!-- Business Address-->
                <p class="m-0 font-size-12">Business Address</p>
                <p class="mb-3 font-size-15 text-dark">{{isEmpty(businessDetails.businessLocation) ? '--' : businessDetails.businessLocation}}</p>
              </div><!-- Business Address-->
              <div class="col-sm-6 col-xs-12" v-if="isFulldetails"><!-- Source of Funds-->
                <p class="m-0 font-size-12">Source of Funds</p>
                <p class="mb-3 font-size-15 text-dark">?</p>
              </div><!-- Source of Funds-->
              <div class="col-sm-6 col-xs-12" v-if="isFulldetails"><!-- What industry is your business in-->
                <p class="m-0 font-size-12">What industry is your business in</p>
                <p class="mb-3 font-size-15 text-dark">{{isEmpty(businessDetails.businessSubType) ? '--' : businessDetails.businessSubType}}</p>
              </div><!-- What industry is your business in-->
              <div class="col-sm-6 col-xs-12" v-if="isFulldetails"><!-- Estimated Ave. Monthly Revenue-->
                <p class="m-0 font-size-12">Estimated Ave. Monthly Revenue</p>
                <p class="mb-3 font-size-15 text-dark">?</p>
              </div><!-- Estimated Ave. Monthly Revenue-->
              <div class="col-sm-6 col-xs-12" v-if="isFulldetails"><!-- Nature of Work-->
                <p class="m-0 font-size-12">Nature of Work</p>
                <p class="mb-3 font-size-15 text-dark">?</p>
              </div><!-- Nature of Work-->
              <div class="col-sm-6 col-xs-12" v-if="isFulldetails"><!-- How long have you been in business-->
                <p class="m-0 font-size-12">How long have you been in business</p>
                <p class="mb-3 font-size-15 text-dark">?</p>
              </div><!-- How long have you been in business-->
              <div class="col-sm-6 col-xs-12" v-if="isFulldetails"><!-- Do you have a physical store-->
                <p class="m-0 font-size-12">Do you have a physical store</p>
                <p class="mb-3 font-size-15 text-dark">?</p>
              </div><!-- Do you have a physical store-->
              <div class="col-sm-6 col-xs-12" v-if="isFulldetails"><!-- How many employees  do you have-->
                <p class="m-0 font-size-12">How many employees  do you have</p>
                <p class="mb-3 font-size-15 text-dark">?</p>
              </div><!-- How many employees  do you have-->
              <div class="col-sm-6 col-xs-12" v-if="isFulldetails"><!-- What are you selling-->
                <p class="m-0 font-size-12">What are you selling</p>
                <p class="mb-3 font-size-15 text-dark">?</p>
              </div><!-- What are you selling-->

            </div>
          </b-tab>

          <b-tab><!-- user documents (Account Type requirement docs) -->
            <template v-slot:title>
              <span class="d-none d-sm-inline-block">User Documents</span>
            </template>

            <div class="at-docs">
              <!-- One card per account-type change, newest first -->
              <div class="at-doc-change" v-for="change in accountTypeHistory" :key="change.changeUuid">
                <div class="d-flex justify-content-between flex-wrap at-doc-head">
                  <span class="font-size-13">Edited by <b class="text-dark">{{ change.editedByEmail }}</b></span>
                  <span class="font-size-13">Date &amp; Time: <b class="text-dark">{{ formatChangeDateTime(change.createdAt) }}</b></span>
                </div>

                <p class="font-size-14 mt-2 mb-1">Account Type: <span class="text-dark">{{ tierLabel(change.accountType) }}</span></p>

                <div class="d-flex justify-content-between align-items-center at-doc-reqs">
                  <span class="fw-bold text-dark">Requirements for {{ tierLabel(change.accountType) }} Account Type &ndash; Documents:</span>
                  <span class="font-size-13">({{ change.documents.length }} attached)</span>
                </div>

                <div class="at-doc-row" v-for="doc in change.documents" :key="doc.documentId">
                  <i class="bx bxs-file at-doc-icon"></i>
                  <div class="at-doc-body">
                    <div class="at-doc-name">{{ doc.fileName }}</div>
                    <div class="font-size-12 text-muted">{{ formatDocSize(doc.sizeBytes) }}</div>
                  </div>
                  <a href="javascript:void(0);" class="at-doc-download" @click="onDownloadAccountTypeDoc(doc)">
                    Download <i class="bx bx-download"></i>
                  </a>
                </div>
              </div>

              <!-- Empty state: merchant never uploaded account-type requirement docs (e.g. still Micro) -->
              <p class="text-muted text-center my-4" v-if="!accountTypeHistory || !accountTypeHistory.length">
                No documents to display.
              </p>
            </div>
          </b-tab><!-- user documents -->
        </b-tabs><!-- business information -->
      </div>
      <b-card-footer>
        <span class="fw-semibold me-2">Change Log:</span>
        <b-link class="small fw-semibold" @click="openModal('bv-modal-cif-logs')">View</b-link>
      </b-card-footer>
    </div>


    <!-- MODAL -->
    <b-modal id="bv-modal-cif-logs" title="Reason Log" size="lg" 
             centered hide-header hide-title hide-footer hide-header-close no-close-on-backdrop>
      
      <cif-modal-logs/>
      
      <div class="text-end mt-3 mb-1">
        <b-button class="w-sm"
                  type="button"
                  variant="light"
                  @click="closeModal('bv-modal-cif-logs')"
                  >Close
        </b-button>
      </div>
    </b-modal><!--bv-modal-cif-logs-->
  </div> <!-- end of outer div-->
</template>


<style scoped>
/* Account Type "User Documents" tab — one bordered card per account-type change. */
.at-doc-change {
  border: 1px solid #ededed;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
}
.at-doc-head {
  gap: 0.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}
.at-doc-reqs {
  margin: 0.5rem 0 0.25rem;
}
.at-doc-row {
  display: flex;
  align-items: center;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f5f5f5;
}
.at-doc-row:last-child {
  border-bottom: none;
}
.at-doc-icon {
  font-size: 22px;
  color: #e83092; /* secondary-500 */
  margin-right: 0.6rem;
}
.at-doc-body {
  flex: 1;
  min-width: 0;
}
.at-doc-name {
  font-weight: 500;
  font-size: 14px;
  color: #202020;
}
.at-doc-download {
  color: #e83092; /* secondary-500 */
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
}
</style>
